import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const UsersTab = ({ users, updateUserRole, banUser, deleteUser }) => {
  const [expandedUser, setExpandedUser] = useState(null);
  const [showWardModal, setShowWardModal] = useState(false);
  const [pendingWardUser, setPendingWardUser] = useState(null);
  const [selectedWard, setSelectedWard] = useState('');
  const [suspendModal, setSuspendModal] = useState(null); // user object
  const [suspendDays, setSuspendDays] = useState(7);
  const [suspendReason, setSuspendReason] = useState('TOS Infraction');
  const [suspending, setSuspending] = useState(false);
  const [awardModal, setAwardModal] = useState(null); // user object
  const [awardPoints, setAwardPoints] = useState(100);
  const [awardReason, setAwardReason] = useState('');
  const [awarding, setAwarding] = useState(false);

  const handleAwardPoints = async () => {
    if (!awardModal || !awardPoints) return;
    setAwarding(true);
    try {
      await api.post(`/admin/users/${awardModal.id}/award-points`, {
        points: parseInt(awardPoints),
        reason: awardReason || 'Admin award'
      });
      toast.success(`${awardPoints} points awarded to ${awardModal.username || awardModal.name}`);
      setAwardModal(null);
      setAwardPoints(100);
      setAwardReason('');
      updateUserRole(awardModal.id, awardModal.role); // triggers parent refresh
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to award points');
    } finally {
      setAwarding(false);
    }
  };

  const wardOptions = ['Ward 1', 'Ward 2', 'Ward 3', 'Ward 4', 'Ward 5', 'Ward 6', 'Ward 7', 'Ward 8', 'Ward 9', 'Ward 10'];

  const handleSuspend = async () => {
    if (!suspendModal) return;
    setSuspending(true);
    try {
      const res = await api.post(`/admin/users/${suspendModal.id}/suspend`, {
        days: suspendDays,
        reason: suspendReason
      });
      toast.success(res.data?.message || `User suspended for ${suspendDays} days`);
      setSuspendModal(null);
      updateUserRole(suspendModal.id, suspendModal.role); // triggers fetchData via parent
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setSuspending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-white tracking-tight">USER MANAGEMENT</h2>
        <div className="text-gray-500 font-mono text-sm">{users.length} total users</div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-800 text-left">
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400 w-8"></th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">User</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Email</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Role</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Ward</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Points</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Status</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400">Joined</th>
              <th className="px-4 py-3 font-mono text-xs uppercase text-gray-400 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const rowBgClass = u.role === 'admin' 
                ? 'bg-purple-900/20 border-l-4 border-l-purple-500' 
                : u.role === 'community_leader' 
                  ? 'bg-blue-900/20 border-l-4 border-l-blue-500' 
                  : '';
              
              const hasShippingInfo = u.shipping_address_line1 || u.shipping_name;
              const isExpanded = expandedUser === u.id;
              
              return (
                <React.Fragment key={u.id}>
                  <tr className={`border-t border-gray-800 hover:bg-gray-850 ${rowBgClass}`}>
                    <td className="px-2 py-3">
                      <button
                        onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                        title={isExpanded ? "Collapse" : "Expand details"}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-display text-sm text-white">
                        <Link
                          to={`/profile/${u.id}`}
                          className="hover:text-[#C5A52A] transition-colors"
                          title="View profile"
                        >
                          {u.name}
                        </Link>
                      </div>
                      {u.user_badges && u.user_badges.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {u.user_badges.slice(0, 4).map((badge, i) => (
                            <span key={i} title={badge.badge_name || badge} className="text-xs">
                              {badge.icon_url?.startsWith('http') ? (
                                <img src={badge.icon_url} alt="" className="w-4 h-4 inline" />
                              ) : (
                                badge.icon_url || '🏅'
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {u.violation_count > 0 && (
                        <div className="mt-1">
                          <span className="text-xs bg-red-900/30 text-red-500 px-2 py-0.5 rounded border border-red-800/50">
                            ⚠️ {u.violation_count} violation{u.violation_count > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      {hasShippingInfo && (
                        <div className="mt-1">
                          <span className="text-xs text-green-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Has shipping info
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-gray-400">{u.email}</div>
                      {!u.is_verified && (
                        <button
                          onClick={() => updateUserRole(u.id, u.role, u.ward, 'verify')}
                          className="text-xs text-yellow-400 hover:text-yellow-300 underline mt-0.5 block"
                          title="Manually verify this user's account"
                        >
                          ⚠ Unverified — click to verify
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => {
                          const newRole = e.target.value;
                          if (newRole === 'community_leader' && !u.ward) {
                            setPendingWardUser(u);
                            setSelectedWard('');
                            setShowWardModal(true);
                            return;
                          }
                          updateUserRole(u.id, newRole, newRole === 'community_leader' ? u.ward : null);
                        }}
                        className={`border text-xs px-2 py-1 rounded ${
                          u.role === 'admin' 
                            ? 'bg-purple-900/50 border-purple-700 text-purple-200' 
                            : u.role === 'community_leader' 
                              ? 'bg-blue-900/50 border-blue-700 text-blue-200' 
                              : 'bg-gray-800 border-gray-700 text-white'
                        }`}
                        data-testid={`user-role-select-${u.id}`}
                      >
                        <option value="user">User</option>
                        <option value="community_leader">Community Leader</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">
                      {u.ward || '-'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#C5A52A]">
                      {u.total_points ? `${u.total_points} pts` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_banned ? (
                        <span className="px-2 py-1 bg-red-900/30 border border-red-800 rounded text-red-500 text-xs font-mono">
                          BANNED
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-green-900/30 border border-green-800 rounded text-green-500 text-xs font-mono">
                          ACTIVE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {u.is_banned ? (
                          <button
                            onClick={() => banUser(u.id, false)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                          >
                            Unban
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => { setSuspendModal(u); setSuspendDays(7); setSuspendReason('TOS Infraction'); }}
                              className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors"
                              data-testid={`suspend-btn-${u.id}`}
                            >
                              Suspend
                            </button>
                            <button
                              onClick={() => { setAwardModal(u); setAwardPoints(100); setAwardReason(''); }}
                              className="px-3 py-1 bg-[#C5A52A] hover:bg-yellow-400 text-black text-xs rounded transition-colors font-bold"
                              data-testid={`award-pts-btn-${u.id}`}
                            >
                              +Pts
                            </button>
                            <button
                              onClick={() => banUser(u.id, true)}
                              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-colors"
                            >
                              Ban
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <tr className="border-t border-gray-800/50">
                      <td colSpan={9} className="px-4 py-4 bg-gray-800/30">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Shipping Info */}
                          <div className="bg-gray-900/50 rounded p-4">
                            <h4 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-green-400" />
                              Saved Shipping Address
                            </h4>
                            {hasShippingInfo ? (
                              <div className="text-sm space-y-1">
                                {u.shipping_name && (
                                  <p className="text-white font-medium">{u.shipping_name}</p>
                                )}
                                {u.shipping_address_line1 && (
                                  <p className="text-gray-400">{u.shipping_address_line1}</p>
                                )}
                                {u.shipping_address_line2 && (
                                  <p className="text-gray-400">{u.shipping_address_line2}</p>
                                )}
                                {(u.shipping_city || u.shipping_province || u.shipping_postal_code) && (
                                  <p className="text-gray-400">
                                    {[u.shipping_city, u.shipping_province, u.shipping_postal_code].filter(Boolean).join(', ')}
                                  </p>
                                )}
                                {u.shipping_phone && (
                                  <p className="text-gray-500 flex items-center gap-1 mt-2">
                                    <Phone className="w-3 h-3" /> {u.shipping_phone}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No shipping address on file</p>
                            )}
                          </div>
                          
                          {/* User Stats */}
                          <div className="bg-gray-900/50 rounded p-4">
                            <h4 className="text-sm font-bold text-gray-300 mb-3">User Stats</h4>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-gray-500">User ID</p>
                                <p className="text-gray-400 font-mono text-xs break-all">{u.id}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Total Points</p>
                                <p className="text-[#C5A52A]">{u.total_points || 0}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Posts</p>
                                <p className="text-gray-400">{u.post_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Verified</p>
                                <p className={u.is_verified ? "text-green-400" : "text-yellow-400"}>
                                  {u.is_verified ? "Yes" : "No"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showWardModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="assign-ward-modal">
          <div className="bg-gray-900 border border-gray-800 rounded p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-2">Assign Ward for Community Leader</h3>
            <p className="text-gray-500 text-xs mb-4">
              {pendingWardUser?.name || 'This user'} needs a ward before becoming a community leader.
            </p>
            <label className="block text-xs text-gray-400 mb-2" htmlFor="assign-ward-select">
              Select Ward
            </label>
            <select
              id="assign-ward-select"
              value={selectedWard}
              onChange={(e) => setSelectedWard(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
              data-testid="assign-ward-select"
            >
              <option value="">-- Select Ward --</option>
              {wardOptions.map((ward) => (
                <option key={ward} value={ward}>{ward}</option>
              ))}
            </select>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={async () => {
                  if (!selectedWard) {
                    toast.error('Please select a ward');
                    return;
                  }
                  await updateUserRole(pendingWardUser.id, 'community_leader', selectedWard);
                  setShowWardModal(false);
                  setPendingWardUser(null);
                  setSelectedWard('');
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-black px-4 py-2 font-mono text-xs uppercase rounded-sm"
                data-testid="assign-ward-confirm"
              >
                Assign Leader
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowWardModal(false);
                  setPendingWardUser(null);
                  setSelectedWard('');
                }}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 font-mono text-xs uppercase rounded-sm"
                data-testid="assign-ward-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {suspendModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="suspend-modal">
          <div className="bg-gray-900 border border-orange-800/60 rounded p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-1">Suspend User</h3>
            <p className="text-gray-400 text-sm mb-4">
              Suspending <strong className="text-orange-400">{suspendModal.name}</strong>. They will see the reason when they try to log in.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Suspend for how many days?</label>
                <input
                  type="number"
                  value={suspendDays}
                  onChange={e => setSuspendDays(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={365}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm"
                  data-testid="suspend-days-input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">Reason (shown to user)</label>
                <select
                  value={suspendReason}
                  onChange={e => setSuspendReason(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm mb-2"
                  data-testid="suspend-reason-select"
                >
                  <option>TOS Infraction</option>
                  <option>Inappropriate Content</option>
                  <option>Harassment</option>
                  <option>Spam</option>
                  <option>Misinformation</option>
                  <option>Other</option>
                </select>
                <input
                  type="text"
                  value={suspendReason}
                  onChange={e => setSuspendReason(e.target.value)}
                  placeholder="Or type a custom reason..."
                  className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm"
                  data-testid="suspend-reason-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSuspend}
                disabled={suspending}
                className="flex-1 bg-orange-600 hover:bg-orange-500 text-white font-mono text-sm py-2 px-4 rounded disabled:opacity-50 transition-colors"
                data-testid="confirm-suspend-btn"
              >
                {suspending ? 'Suspending...' : `Suspend ${suspendDays} Day${suspendDays !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => setSuspendModal(null)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-mono text-sm py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Award Points Modal */}
      {awardModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-[#C5A52A]/40 rounded p-6 max-w-sm w-full">
            <h3 className="text-white font-bold font-mono mb-1">Award Points</h3>
            <p className="text-gray-400 text-xs font-mono mb-4">
              To: <span className="text-[#C5A52A]">{awardModal.username || awardModal.name}</span>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 font-mono uppercase tracking-wider block mb-1">Points to Award</label>
                <input
                  type="number" min="1" max="10000"
                  value={awardPoints}
                  onChange={e => setAwardPoints(e.target.value)}
                  className="w-full bg-black border border-[#2a2a2a] text-white font-mono text-sm px-3 py-2 rounded focus:border-[#C5A52A] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-mono uppercase tracking-wider block mb-1">Reason (optional)</label>
                <input
                  type="text" placeholder="e.g. Top contributor this month"
                  value={awardReason}
                  onChange={e => setAwardReason(e.target.value)}
                  className="w-full bg-black border border-[#2a2a2a] text-white font-mono text-sm px-3 py-2 rounded focus:border-[#C5A52A] focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleAwardPoints}
                disabled={awarding || !awardPoints}
                className="flex-1 bg-[#C5A52A] hover:bg-yellow-400 text-black font-mono text-sm py-2 px-4 rounded font-bold disabled:opacity-50 transition-colors"
              >
                {awarding ? 'Awarding...' : `Award ${awardPoints} pts`}
              </button>
              <button
                onClick={() => setAwardModal(null)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-mono text-sm py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
